// Öffentliche Störungen (Konzept Abschnitt 5 und 9): Entwurf anlegen, Updates,
// veröffentlichen, beheben; Komponentenstatus der Statuspage.
import { useState } from 'react';

import { fmt, PRODUCTS, productName, slug } from './api.ts';
import type { Api, ComponentStatus, Incident, IncidentState, ProductId, State, Text } from './api.ts';

const STATE_LABEL: Record<IncidentState, string> = {
  investigating: 'Wird untersucht',
  identified: 'Ursache identifiziert',
  monitoring: 'Wird beobachtet',
  resolved: 'Behoben',
};
const STATUS_LABEL: Record<ComponentStatus, string> = {
  operational: 'Betriebsbereit',
  degraded: 'Eingeschränkt',
  outage: 'Störung',
  maintenance: 'Wartung',
};

type Props = { api: Api; state: State; roles: string[]; notify: (msg: string, error?: boolean) => void; reload: () => void };

function TextFields({ value, onChange, label }: { value: Text; onChange: (t: Text) => void; label: string }) {
  return (
    <div className="row">
      <label>
        {label} (DE)
        <textarea rows={2} value={value.de} onChange={(e) => onChange({ ...value, de: e.target.value })} />
      </label>
      <label>
        {label} (EN)
        <textarea rows={2} value={value.en} onChange={(e) => onChange({ ...value, en: e.target.value })} />
      </label>
    </div>
  );
}

function IncidentCard({ inc, api, version, roles, notify, reload }: { inc: Incident; version: number } & Omit<Props, 'state'>) {
  const [msg, setMsg] = useState<Text>({ de: '', en: '' });
  const [st, setSt] = useState<Exclude<IncidentState, 'resolved'>>('identified');
  const canPublish = roles.includes('publisher');
  const mayEdit = roles.includes('editor') && (!inc.published || canPublish);

  const run = async (what: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      notify(what);
      reload();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), true);
    }
  };
  const textOk = msg.de.trim() && msg.en.trim();

  return (
    <article className="card">
      <header className="row-between">
        <h3>{inc.title.de}</h3>
        <span className={`pill ${inc.published ? 'live' : 'draft'}`}>{inc.published ? 'veröffentlicht' : 'Entwurf'}</span>
      </header>
      <p className="meta">
        {inc.id} · seit {fmt(inc.started_at)} · {inc.severity === 'outage' ? 'Störung' : 'Eingeschränkt'} ·{' '}
        {inc.affected_components.map(productName).join(', ')}
        {inc.internal_ref && <> · intern: {inc.internal_ref}</>}
      </p>
      <ol className="timeline">
        {[...inc.updates]
          .sort((a, b) => b.at.localeCompare(a.at))
          .map((u) => (
            <li key={u.id}>
              <b>{STATE_LABEL[u.state]}</b> {fmt(u.at)} – {u.message.de}
            </li>
          ))}
      </ol>
      {mayEdit && !inc.resolved_at && (
        <>
          <TextFields label="Neues Update" value={msg} onChange={setMsg} />
          <div className="actions">
            <select value={st} onChange={(e) => setSt(e.target.value as typeof st)}>
              {(['investigating', 'identified', 'monitoring'] as const).map((s) => (
                <option key={s} value={s}>
                  {STATE_LABEL[s]}
                </option>
              ))}
            </select>
            <button
              disabled={!textOk}
              onClick={() =>
                void run('Update gespeichert.', () =>
                  api.addUpdate(inc.id, { id: `u-${Date.now().toString(36)}`, state: st, message: msg }),
                )
              }
            >
              Update hinzufügen
            </button>
            <button disabled={!textOk} onClick={() => void run('Störung behoben.', () => api.resolveIncident(inc.id, msg))}>
              Als behoben melden
            </button>
            {!inc.published && canPublish && (
              <button
                className="primary"
                disabled={!inc.updates.length}
                onClick={() =>
                  void run('Auf der Statuspage veröffentlicht.', () =>
                    api.putIncident(
                      inc.id,
                      {
                        severity: inc.severity,
                        affected_components: inc.affected_components,
                        started_at: inc.started_at,
                        title: inc.title,
                        published: true,
                        ...(inc.internal_ref ? { internal_ref: inc.internal_ref } : {}),
                      },
                      version,
                    ),
                  )
                }
              >
                Auf Statuspage veröffentlichen
              </button>
            )}
          </div>
        </>
      )}
    </article>
  );
}

export function Incidents({ api, state, roles, notify, reload }: Props) {
  const [title, setTitle] = useState<Text>({ de: '', en: '' });
  const [components, setComponents] = useState<ProductId[]>([]);
  const [severity, setSeverity] = useState<'degraded' | 'outage'>('outage');
  const open = Object.values(state.incidents)
    .filter((i) => !i.resolved_at)
    .sort((a, b) => b.started_at.localeCompare(a.started_at));
  const canEdit = roles.includes('editor');
  const canPublish = roles.includes('publisher');

  async function create() {
    if (!title.de.trim() || !title.en.trim() || !components.length)
      return notify('Titel (DE/EN) und betroffene Anwendungen angeben.', true);
    const now = new Date();
    const id = `inc-${now.toISOString().slice(0, 10)}-${slug(title.de) || 'stoerung'}`;
    try {
      await api.putIncident(
        id,
        {
          severity,
          affected_components: components,
          started_at: now.toISOString().replace(/\.\d{3}Z$/, 'Z'),
          title,
          published: false,
        },
        state.version,
      );
      notify(`Entwurf ${id} angelegt. Erstes Update schreiben, dann veröffentlichen.`);
      setTitle({ de: '', en: '' });
      setComponents([]);
      reload();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), true);
    }
  }

  return (
    <>
      <section className="card">
        <h2>Anwendungen auf der Statuspage</h2>
        <table>
          <tbody>
            {PRODUCTS.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>
                  <select
                    disabled={!canPublish}
                    value={state.components[p.id]?.status ?? 'operational'}
                    onChange={(e) =>
                      void api
                        .setComponent(p.id, e.target.value as ComponentStatus)
                        .then(() => {
                          notify(`${p.name}: ${STATUS_LABEL[e.target.value as ComponentStatus]}`);
                          reload();
                        })
                        .catch((err: unknown) => notify(err instanceof Error ? err.message : String(err), true))
                    }
                  >
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!canPublish && <small>Keine Berechtigung zum Ändern.</small>}
      </section>

      {canEdit && (
        <section className="card">
          <h2>Neue Störung (Entwurf)</h2>
          <TextFields label="Titel" value={title} onChange={setTitle} />
          <div className="chips">
            {PRODUCTS.map((p) => (
              <label key={p.id} className="inline">
                <input
                  type="checkbox"
                  checked={components.includes(p.id)}
                  onChange={() => setComponents((c) => (c.includes(p.id) ? c.filter((x) => x !== p.id) : [...c, p.id]))}
                />{' '}
                {p.name}
              </label>
            ))}
          </div>
          <div className="actions">
            <select value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)}>
              <option value="outage">Störung</option>
              <option value="degraded">Eingeschränkt</option>
            </select>
            <button className="primary" onClick={() => void create()}>
              Entwurf anlegen
            </button>
          </div>
        </section>
      )}

      <h2>Offene Störungen</h2>
      {open.length ? (
        open.map((i) => (
          <IncidentCard key={i.id} inc={i} api={api} version={state.version} roles={roles} notify={notify} reload={reload} />
        ))
      ) : (
        <p className="empty">Keine offenen Störungen.</p>
      )}
    </>
  );
}
